import React, { useEffect, useRef, useState } from 'react';
import { Animated, ImageBackground, Linking, View } from 'react-native';

import B from '../../../components/Bold';
import { Button, Text, TextInput, useTheme } from 'react-native-paper';
import { NavigationWithProps } from '../../../core/Types';
import { LoginNavParamList } from '../Login';
import { useToast } from 'react-native-toast-notifications';
import { useAuth } from '../../../core/api/auth';
import { hasLengthBetween, isEmpty, isUndefinedOrNull, isValidEmail, isValidPassword } from '../../../core/forms/forms';
import TextFormField from '../../../components/TextFormField';

const SignUpEmailStep: React.FC<NavigationWithProps<{}, LoginNavParamList, "SignUpEmail">> = ({ navigation, route }) => {
    const opacityAnim = useRef(new Animated.Value(0)).current;

    const { therapistId, clinicId } = route.params || {};

    const theme = useTheme();
    const auth = useAuth();
    const toast = useToast();

    const [resendCountDown, setResendCountDown] = useState<number>(0);

    const [hasError, setHasError] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    const [token, setToken] = useState<string>(route.params.verif || "");
    const [fullName, setFullName] = useState<string>("");
    const [email, setEmail] = useState<string>(route.params.email || "");
    const [password, setPassword] = useState<string>("");
    const [verifCode, setVerifCode] = useState<string>("");

    const signUp = async () => {
        setLoading(true);

        auth.emailSignUp({
            full_name: fullName,
            email: email,
            password: password,
            verification_code: verifCode,
            verification_token: token,
            accept_terms: true,
        }).then(() => {
            console.log('Success:', 'Cuenta creada');
            toast.show("Cuenta creada exitosamente", { duration: 1000 });
            setTimeout(() => {
                if (therapistId && clinicId) {
                    location.replace(`/therapist?id=${therapistId}&clinic=${clinicId}`);
                } else {
                    location.replace("/");
                }
            }, 1000);
        }).catch(() => {
            onError();
        }).finally(() => {
            setLoading(false);
        });

    };

    const onError = () => {
        console.log('Error:', 'Error');
        toast.show("Código de verificación incorrecto", { duration: 1000 });
        setVerifCode("");
    };

    const openTermsAndConditions = () => {
        Linking.openURL("https://psicopro.co/terminos-y-condiciones/");
    };

    const openPrivacyPolicy = () => {
        Linking.openURL("https://psicopro.co/politica-de-privacidad/");
    };

    const validateName = (name: string): string | undefined => {
        if (isUndefinedOrNull(name) || isEmpty(name)) {
            return "El nombre es obligatorio";
        }

        if (!hasLengthBetween(name, 3, 100)) {
            return "El nombre debe tener entre 3 y 100 caracteres";
        }
    };

    const validateEmail = (email: string): string | undefined => {
        if (isUndefinedOrNull(email) || isEmpty(email)) {
            return "El email es obligatorio";
        }

        if (!isValidEmail(email)) {
            return "El email no es válido";
        }
    };

    const validatePassword = (password: string): string | undefined => {
        if (isUndefinedOrNull(password) || isEmpty(password)) {
            return "La contraseña es obligatoria";
        }

        if (!hasLengthBetween(password, 8, 100)) {
            return "La contraseña debe tener entre 8 y 100 caracteres";
        }

        if (!isValidPassword(password)) {
            return "Una mayúscula, una minúscula, un número y un carácter especial";
        }
    };

    const resendVerificationCode = () => {
        auth.emailVerificationForSignup(email)
            .then((verificationToken: string) => {
                toast.show("Código de verificación reenviado", { duration: 1000 });
                setVerifCode("");
                setToken(verificationToken);
                initCountdown(59);
            })
            .catch((e) => {
                console.error("Error during email verification:", e);
                toast.show("Error al enviar el código de verificación", { duration: 1000 });
            });
    };

    const initCountdown = (seconds: number) => {
        setResendCountDown(seconds);

        const interval = setInterval(() => {
            setResendCountDown((prev) => {
                if (prev <= 0) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        setTimeout(() => {
            return () => clearInterval(interval);
        }, 60000);
    }

    useEffect(() => {
        Animated.timing(opacityAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
        }).start();

        if (token) {
            initCountdown(59);
        }
    }, []);

    return (
        <Animated.View style={{ height: "100%", opacity: opacityAnim }}>
            <ImageBackground
                style={{
                    height: "100%",
                    flexDirection: 'column',
                }}
                source={require("../../../../assets/img/scenes/fondo_marco.png")}>

                <View style={{
                    marginTop: 10,
                }}>
                    <Button
                        style={{ width: 100, marginLeft: 10 }}
                        icon="arrow-left"
                        textColor={theme.colors.primary}
                        onPress={() => navigation.goBack()}>
                        Volver
                    </Button>
                </View>

                <View style={{
                    marginTop: 100,
                }}>
                    <Text style={{
                        fontSize: 20,
                        textAlign: 'center',
                        marginBottom: 10,
                        color: theme.colors.onTertiaryContainer,
                    }}>
                        <B>Regístrate para continuar</B>
                    </Text>
                    <View style={{
                        marginHorizontal: 20,
                        marginBottom: 10
                    }}>
                        <TextFormField
                            label="Nombre completo"
                            value={fullName}
                            onChangeText={setFullName}
                            validators={[validateName]}
                            setHasError={setHasError}
                            mode="outlined"
                        />
                        <TextFormField
                            label="Email"
                            value={email}
                            onChangeText={setEmail}
                            validators={[validateEmail]}
                            setHasError={setHasError}
                            mode="outlined"
                        />
                        <TextFormField
                            label="Contraseña"
                            value={password}
                            secureTextEntry={true}
                            onChangeText={setPassword}
                            validators={[validatePassword]}
                            setHasError={setHasError}
                            mode="outlined"
                        />
                        <View>
                            <TextInput
                                label="Código de verificación"
                                value={verifCode}
                                onChangeText={setVerifCode}
                                mode="outlined"
                            />
                            <Text style={{ marginTop: 5, marginBottom: 10, textAlign: 'right' }}>
                                {resendCountDown > 0 ?
                                    <>Reenviar código en <Text style={{ color: theme.colors.primary }}> 00:{resendCountDown.toString().padStart(2, '0')} </Text></>
                                    : <Button mode='text' onPress={resendVerificationCode}>Reenviar código</Button>}
                            </Text>
                        </View>
                        <Button
                            style={{ borderRadius: 5 }}
                            mode="contained"
                            onPress={signUp}
                            loading={loading}
                            disabled={hasError || !verifCode || !fullName || !password}
                        >
                            Crear cuenta
                        </Button>
                    </View>
                </View>
                <Text style={{
                    marginTop: "auto",
                    marginBottom: 130,
                    fontSize: 18,
                    textAlign: "center",

                }}>
                    Continuando aceptas los <Text onPress={openTermsAndConditions}><B>Términos y Condiciones</B> </Text>
                    y la <Text onPress={openPrivacyPolicy}><B>Política de privacidad</B></Text>.
                </Text>
            </ImageBackground>
        </Animated.View >
    );
}

export default SignUpEmailStep;
